import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getEmailAuth0, getTenantIdAuth0, isAdminAuth0, getDistributorAuth0 } from "../authUtils";
import { getUserMetadataBySubId, getUserInfoBySubId } from "../api/tenantApi";

const initialState = {
  subId: null,
  tenantId: null,
  isAdmin: false,
  isDistributor: false,
  distributorId: null,
  email:null,
  status: 'idle',
  identityStatus: 'idle',
  identityRequestId: null,
  legacyRequests: {},
};

export const fetchAdminStatus = createAsyncThunk(
  'userInfo/fetchAdminStatus',
  async () => {
    const isAdmin = await isAdminAuth0();
    return isAdmin;
  }
);

export const fetchTenantId = createAsyncThunk(
  'userInfo/fetchTenantId',
  async () => {
    const tenantId = await getTenantIdAuth0();
    return tenantId;
  }
);
export const fetchEmail = createAsyncThunk(
  'userInfo/fetchEmail',
  async () => {
    const email = await getEmailAuth0();
    return email;
  }
);
export const fetchDistributor = createAsyncThunk(
  'userInfo/fetchDistributor',
  async () => {
    const d = await getDistributorAuth0();
    return d; // { isDistributor, distributorId }
  }
);

// This signal is for routing and request scoping, never a server permission grant.
// Always compare it with Auth0's current subject, not with a cached subject.
export const selectRequesterIdentityReady = (state, subject) => (
  typeof subject === 'string' && subject.length > 0
  && state.userInfo?.subId === subject
  && state.userInfo?.identityStatus === 'ready'
);

export const fetchRequesterIdentity = createAsyncThunk(
  'userInfo/fetchRequesterIdentity',
  async (subject) => {
    if (typeof subject !== 'string' || !subject) throw new Error('Authenticated subject is required');
    // The legacy helpers intentionally fall back to cached roles after errors.
    // Such a fallback must not settle a new identity's routing readiness.
    const metadata = await getUserMetadataBySubId(subject);
    if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)
      || (metadata.isAdmin !== undefined && typeof metadata.isAdmin !== 'boolean')
      || (metadata.isDistributor !== undefined && typeof metadata.isDistributor !== 'boolean')
      || (metadata.tenantId != null && typeof metadata.tenantId !== 'string')
      || (metadata.distributorId != null && typeof metadata.distributorId !== 'string')
      || (metadata.isDistributor === true && !metadata.distributorId)) {
      throw new Error('Authenticated role metadata is unavailable');
    }
    // Email is display metadata, not an authorization or readiness input.
    const userInfo = await getUserInfoBySubId(subject).catch(() => null);
    return {
      isAdmin: metadata.isAdmin === true,
      isDistributor: metadata.isDistributor === true,
      tenantId: metadata.tenantId ?? null,
      distributorId: metadata.distributorId ?? null,
      email: typeof userInfo?.email === 'string' ? userInfo.email : null,
    };
  },
  { condition: (subject, { getState }) => getState().userInfo.subId === subject }
);

const resetIdentity = (state, subject) => {
  Object.assign(state, initialState, {
    subId: typeof subject === 'string' && subject ? subject : null,
    identityStatus: subject ? 'loading' : 'idle',
    status: subject ? 'loading' : 'idle',
    legacyRequests: {},
  });
};

const legacyPending = (field) => (state, action) => {
  if (state.identityStatus !== 'idle' || !state.subId) return;
  state.legacyRequests[field] = { requestId: action.meta.requestId, subject: state.subId };
  state.status = 'loading';
};
const legacyMatches = (state, action, field) => (
  state.identityStatus === 'idle' && state.subId
  && state.legacyRequests[field]?.requestId === action.meta.requestId
  && state.legacyRequests[field]?.subject === state.subId
);

export const userInfoSlice = createSlice({
  name: "userInfo",
  initialState,
  reducers: {
    beginRequesterIdentity: (state, action) => resetIdentity(state, action.payload),
    setSubId: (state, action) => {
      if (state.subId !== action.payload) resetIdentity(state, action.payload);
    },
    setAdminStatus: (state, action) => { //
      console.log("is admin thunk");
      state.isAdmin = action.payload;
    },
    setTenantId: (state, action) => {
      state.tenantId = action.payload;
    },
    setEmail: (state, action) => {
      state.email = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRequesterIdentity.pending, (state, action) => {
        if (state.subId !== action.meta.arg) return;
        resetIdentity(state, action.meta.arg);
        state.identityRequestId = action.meta.requestId;
      })
      .addCase(fetchRequesterIdentity.fulfilled, (state, action) => {
        if (state.subId !== action.meta.arg || state.identityRequestId !== action.meta.requestId) return;
        Object.assign(state, action.payload, { identityStatus: 'ready', identityRequestId: null, status: 'idle' });
      })
      .addCase(fetchRequesterIdentity.rejected, (state, action) => {
        if (state.subId !== action.meta.arg || state.identityRequestId !== action.meta.requestId) return;
        resetIdentity(state, action.meta.arg);
        state.identityStatus = 'error';
        state.status = 'idle';
      })
      .addCase(fetchAdminStatus.pending, legacyPending('admin'))
      .addCase(fetchAdminStatus.fulfilled, (state, action) => {
        if (!legacyMatches(state, action, 'admin')) return;
        state.status = 'idle';
        state.isAdmin = action.payload;
      })
      .addCase(fetchTenantId.pending, legacyPending('tenant'))
      .addCase(fetchTenantId.fulfilled, (state, action) => {
        if (!legacyMatches(state, action, 'tenant')) return;
        state.status = 'idle';
        state.tenantId = action.payload;
      })
      .addCase(fetchEmail.pending, legacyPending('email'))
      .addCase(fetchEmail.fulfilled, (state, action) => {
        if (!legacyMatches(state, action, 'email')) return;
        state.status = 'idle';
        state.email = action.payload;
      })
      .addCase(fetchDistributor.pending, legacyPending('distributor'))
      .addCase(fetchDistributor.fulfilled, (state, action) => {
        if (!legacyMatches(state, action, 'distributor')) return;
        state.status = 'idle';
        state.isDistributor = !!action.payload?.isDistributor;
        state.distributorId = action.payload?.distributorId ?? null;
      })
  },
});

export const { beginRequesterIdentity, setSubId, setAdminStatus, setTenantId,setEmail } = userInfoSlice.actions;
export default userInfoSlice.reducer;